import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Avatar, Kicker, Bar, Field, Area, Modal, Sparkline, Empty } from "@/components/kit";
import { currentPct, statusColor, fmtDate, CHECKPOINTS, ROMAN } from "@/lib/util";
import { KeyRound } from "lucide-react";

export default function MemberDetail() {
  const { id } = useParams();
  const { me, memberById, goals, commitments, baseline, settings, refreshMembers, canWrite, meetings, yearId } = useTable();
  const m = memberById(id);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [checkins, setCheckins] = useState([]);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinForm, setPinForm] = useState({});

  if (!m) return <Empty line="No such seat." />;

  const canEdit = canWrite && (me.isAdmin || m.id === me.memberId);
  const isMe = m.id === me.memberId;
  const myGoals = goals.filter((g) => g.memberId === m.id);
  const myCommits = commitments.filter((c) => c.memberId === m.id);
  const dims = settings?.dimensions || [];
  const trend = CHECKPOINTS.map((cp) => {
    const row = baseline.find((b) => b.memberId === m.id && b.checkpoint === cp);
    if (!row) return null;
    const vals = dims.map((_, i) => row.scores?.[`d${i + 1}`]).filter(Boolean);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }).filter((v) => v !== null);

  const save = async () => {
    try {
      await api.put(`/members/${m.id}`, form);
      await refreshMembers();
      setOpen(false);
      toast.success("Updated.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePin = async () => {
    try {
      await api.post("/auth/change-pin", {
        currentPin: pinForm.currentPin || "", newPin: pinForm.newPin || "",
      });
      setPinOpen(false);
      toast.success("Your PIN is changed. Use it next time you sign in.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="member-detail">
      <Link to="/members" className="kicker">← Members</Link>
      <div className="flex items-center gap-4 mt-4 mb-6">
        <Avatar name={m.name} color={m.avatarColor} size={56} />
        <div>
          <h1 className="serif text-4xl leading-none">{m.name}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ink-mute)" }}>{m.company}</p>
        </div>
        {canEdit && (
          <button
            className="btn-quiet ml-auto"
            data-testid="edit-member"
            onClick={() => { setForm({ ...m }); setOpen(true); }}
          >
            Edit
          </button>
        )}
        {isMe && (
          <button className="btn-quiet" data-testid="change-pin" onClick={() => { setPinForm({}); setPinOpen(true); }}>
            <KeyRound size={13} /> Change my PIN
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <Kicker>Contact</Kicker>
          <div className="mt-3 space-y-1.5 text-sm">
            <div>{m.email}</div>
            <div>{m.phone || "—"}</div>
            {m.status === "Alumni" && <Chip>Alumni</Chip>}
          </div>
        </Card>
        <Card>
          <Kicker>Executive assistant</Kicker>
          <p className="text-[11px] mt-1 italic" style={{ color: "var(--ink-mute)" }}>logistics only, never forum content</p>
          <div className="mt-3 space-y-1.5 text-sm">
            <div>{m.eaName || "—"}</div>
            <div>{m.eaEmail || "—"}</div>
            <div>{m.eaPhone || "—"}</div>
          </div>
        </Card>
        <Card data-testid="member-table-commitment">
          <Kicker>Commitment to the table · this year</Kicker>
          {(m.tableCommitment || "").trim() ? (
            <ul className="mt-3 space-y-2">
              {m.tableCommitment.split("\n").filter((l) => l.trim()).map((line, i) => (
                <li key={i} className="serif text-base leading-snug flex gap-2">
                  <span style={{ color: "var(--brass)" }}>—</span>
                  <span>{line.replace(/^[-·•]\s*/, "")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm mt-3" style={{ color: "var(--ink-mute)" }}>Not stated yet.</p>
          )}
        </Card>
        <Card>
          <Kicker>Company / billing address</Kicker>
          <p className="text-sm mt-3 whitespace-pre-wrap">{m.companyAddress || "—"}</p>
        </Card>
        <Card>
          <Kicker>Baseline trend</Kicker>
          <div className="mt-4"><Sparkline points={trend} width={200} height={40} /></div>
        </Card>
      </div>

      <div className="mt-8">
        <Kicker className="mb-3">This year's goals</Kicker>
        {myGoals.length === 0 ? (
          <Empty line="No goals recorded for this year." />
        ) : (
          <div className="space-y-3">
            {myGoals.map((g) => (
              <Card key={g.id} className="p-4">
                <div className="flex justify-between gap-3 items-start">
                  <div className="min-w-0">
                    <Chip color={g.category === "Personal" ? "var(--blue)" : "var(--brass)"}>{g.category}</Chip>
                    <p className="serif text-lg mt-2 leading-snug">{g.goal}</p>
                  </div>
                  <span className="serif text-xl">{currentPct(g) === null ? "—" : `${currentPct(g)}%`}</span>
                </div>
                <div className="mt-3"><Bar value={currentPct(g) || 0} color={g.status === "At risk" ? "var(--rust)" : "var(--green)"} /></div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <Kicker className="mb-3">Ledger history</Kicker>
        {myCommits.length === 0 ? (
          <Empty line="Nothing here yet. The close writes this page." />
        ) : (
          <div className="space-y-2">
            {myCommits.map((c) => (
              <Card key={c.id} className="p-4 flex justify-between gap-3 items-start">
                <p className="serif text-base">{c.text}</p>
                <div className="shrink-0 flex flex-col items-end gap-1.5">
                  <Chip color={statusColor(c.status)}>{c.status}</Chip>
                  <span className="text-[11px]" style={{ color: "var(--ink-mute)" }}>{fmtDate(c.dueDate)}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Modal open={pinOpen} onClose={() => setPinOpen(false)} title="Change my PIN" testId="pin-modal">
        <p className="text-[13px] mb-4" style={{ color: "var(--ink-mute)" }}>
          Your PIN is yours alone — nobody at the table can see it. You need your current PIN to set
          a new one. If you have lost it, the Moderator can reset it for you.
        </p>
        <Field label="Current PIN" value={pinForm.currentPin || ""} data-testid="pin-current"
          onChange={(e) => setPinForm({ ...pinForm, currentPin: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
        <Field label="New PIN (four digits)" value={pinForm.newPin || ""} data-testid="pin-new"
          onChange={(e) => setPinForm({ ...pinForm, newPin: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
        <button className="btn-ink w-full" data-testid="pin-save"
          disabled={(pinForm.newPin || "").length !== 4 || (pinForm.currentPin || "").length !== 4}
          onClick={savePin}>Change PIN</button>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Edit details" testId="member-edit-modal">
        {me.isAdmin && (
          <>
            <Field label="Name" value={form.name || ""} data-testid="member-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Field label="Company" value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            <Field label="Email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" />
          </>
        )}
        <Area
          label="Commitment to the table (one per line)"
          value={form.tableCommitment || ""}
          data-testid="member-table-commitment-input"
          onChange={(e) => setForm({ ...form, tableCommitment: e.target.value })}
        />
        <Field label="Phone" value={form.phone || ""} data-testid="member-phone" onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Field label="Company address" value={form.companyAddress || ""} onChange={(e) => setForm({ ...form, companyAddress: e.target.value })} />
        <Field label="EA name" value={form.eaName || ""} onChange={(e) => setForm({ ...form, eaName: e.target.value })} />
        <Field label="EA email" value={form.eaEmail || ""} onChange={(e) => setForm({ ...form, eaEmail: e.target.value })} />
        <Field label="EA phone" value={form.eaPhone || ""} onChange={(e) => setForm({ ...form, eaPhone: e.target.value })} />
        <button className="btn-ink w-full" data-testid="member-save" onClick={save}>Save</button>
      </Modal>
    </div>
  );
}
